// components/CategoryManagement/CategoryForm.jsx
import React, { useState, useEffect } from 'react';
import { searchAttributes } from '../../services/categoryService';
import { useCategoryTree } from "../../context/CategoryTreeContext";
import Button from "../Button";
import {toPersianNumbers} from "../../utils/toPersianNumbers";
import ImageUploader from "../Form/ImageUploader";


const AttributeChip = ({ attr, onRemove }) => (
  <div className="inline-flex items-center gap-2 bg-white border rounded-full px-3 py-1 text-sm mr-2 mb-2">
    <span className="font-medium text-gray-800">{attr.name}</span>
    <button onClick={() => onRemove(attr.id)} className="text-gray-500 hover:text-red-600">
      ✕
    </button>
  </div>
);

const CategoryForm = ({ category, parentCategory, onSubmit, onCancel }) => {
  const { openCategories, toggleCategory } = useCategoryTree();
  const [categoryImage, setCategoryImage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_image:null,
    parent: null,
    code: '',
    is_active: true,
    sort_order: 0,
    icon: null,
    attributes: [] // array of attribute objects {id, name, ...}
  });
  const [errors, setErrors] = useState({});
  const [attrQuery, setAttrQuery] = useState('');
  const [attrResults, setAttrResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        category_image: category.category_image || null,
        parent: category.parent || null,
        code: category.code || '',
        is_active: category.is_active !== undefined ? category.is_active : true,
        sort_order: category.sort_order || 0,
        icon: category.icon || null,
        attributes: (category.attributes || []).map((a) => {
          // backend detail may provide attribute objects or attribute id; normalize
          if (a && a.attribute) return { id: a.attribute.id, name: a.attribute.name };
          if (typeof a === 'number') return { id: a, name: String(a) };
          return a;
        })
      });
    } else if (parentCategory) {
      setFormData((prev) => ({ ...prev, parent: parentCategory.id }));
    }
  }, [category, parentCategory]);

  // attribute search with debounce (simple)
  useEffect(() => {
    const t = setTimeout(() => {
      if (!attrQuery) {
        setAttrResults([]);
        return;
      }
      setSearchLoading(true);
      searchAttributes(attrQuery)
        .then((res) => {
          // backend list returns paginated list of attributes - adapt accordingly
          const data = res.data?.results ?? res.data ?? [];
          // map to minimal shape {id, name}
          setAttrResults(data.map((d) => ({ id: d.id, name: d.name })));
        })
        .catch((e) => {
          console.error('searchAttributes error', e);
          setAttrResults([]);
        })
        .finally(() => setSearchLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [attrQuery]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleAddAttribute = (attr) => {
    if (!attr) return;
    if (formData.attributes.some((a) => a.id === attr.id)) return;
    setFormData((prev) => ({ ...prev, attributes: [...prev.attributes, attr] }));
    setAttrQuery('');
    setAttrResults([]);
  };

  const handleRemoveAttribute = (id) => {
    setFormData((prev) => ({ ...prev, attributes: prev.attributes.filter((a) => a.id !== id) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'عنوان دسته الزامی است';
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    // transform attributes to array of ids for API
    const payload = {
      name: formData.name,
      description: formData.description,
      parent: formData.parent,
      code: formData.code,
      is_active: formData.is_active,
      sort_order: Number(formData.sort_order || 0),
      attributes: formData.attributes.map((a) => a.id)
    };
    if (categoryImage instanceof File) {
      payload["category_image"] = categoryImage;
    }
    onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {category && `ویرایش دسته ${formData.name}`}
            {!category && parentCategory && `افزودن زیر‌دسته به دسته ${parentCategory.name}`}
            {!category && !parentCategory && 'افزودن دسته ریشه'}
          </h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">عنوان دسته *</label>
              <input name="name" value={formData.name} onChange={handleChange}
                     className={`w-full px-3 py-2 border rounded ${errors.name ? 'border-red-500' : 'border-gray-300'}`}/>
              {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">توضیحات</label>
              <textarea name="description" value={formData.description} onChange={handleChange}
                        className="w-full px-3 py-2 border rounded border-gray-300" rows={3}/>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ترتیب نمایش</label>
              <input name="sort_order" type="number" value={formData.sort_order} onChange={handleChange}
                     className="w-full px-3 py-2 border rounded border-gray-300"/>
            </div>

            <div className="flex items-center gap-3">
              <input id="is_active" name="is_active" type="checkbox" checked={formData.is_active}
                     onChange={handleChange}
                     className="h-4 w-4"/>
              <label htmlFor="is_active" className="text-sm">فعال</label>
            </div>
          </div>

          {/* attribute selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">مشخصه‌ها</label>
            <div className="mb-2">
              {formData.attributes.map((a) => (
                  <AttributeChip key={a.id} attr={a} onRemove={handleRemoveAttribute}/>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <input value={attrQuery} onChange={(e) => setAttrQuery(e.target.value)}
                     placeholder="جستجوی مشخصه..." className="flex-1 px-3 py-2 border rounded border-gray-300"/>
              <div className="w-28 text-sm text-gray-500">{searchLoading ? 'در حال جستجو...' : ''}</div>
            </div>

            {/* results */}
            {attrResults.length > 0 && (
                <div className="mt-2 max-h-40 overflow-auto border rounded bg-white p-2">
                  {attrResults.map((r) => (
                      <div key={r.id} className="py-1 px-2 hover:bg-gray-50 cursor-pointer"
                           onClick={() => handleAddAttribute(r)}>
                        {r.name}
                      </div>
                  ))}
                </div>
            )}
          </div>
          <ImageUploader
              imageUrl={category?.category_image|| null}
              onChange={(file) => {
                setFormData({...formData, category_image: file});
                setCategoryImage(file)
              }}

          />


          <div className="flex justify-end gap-3 pt-4">
            <Button variant={'secondary'} onClick={onCancel} children={'انصراف'}/>
            <Button type={'submit'} variant={'primary'} children={category ? 'ویرایش' : 'تایید'}/>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryForm;
